import React, { useContext, useMemo } from 'react';
import {
  TEDEKBokforderSTATUSEnum,
  TEDEKBokforderTYPEnum,
  TEDEKBokforderU_UTBETALNINGSKATEGORIEnum,
} from '../../../../../../globalQueryTypes';
import { IEditMenuAction } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { mergeBusinessDataMutations } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/MergeBusinessDataMutations';
import { IValues } from '../../../../_StandardRutin/Mutations/ObjectMutations/ObjectUpdateMutationBase';
import {
  IFranvaroUserOption,
  certifyOrRetakeRows,
  getAttestanterWithCurrentUserAsReserv,
  getBfoValidationErrors,
  getCertifiedRowsUpdateValues,
  getRowsDeleteObjects,
  getValuesForNewBokforder,
  isSendAndAttestButtonHidden,
  saveRows,
} from '../Utils/BokforderUtils';
import { FranvaroContext } from '../../../../SystemAdministration/ObjektVyer/User/Context/FranvaroContext/FranvaroContext';
import { BokforderType__Associations_Rader } from '../Queries/_QueryTypes/BokforderType';
import { UtanordningType__Associations_Rader } from '../Queries/_QueryTypes/UtanordningType';
import { BokforderWrapperContext } from '../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import { AdditionalAttestDialogOpenEnum } from '../../../Rutiner/Bokforder/Components/BokforderProviderWrapper';

export const useBokforderEditMenuSendAndAttesteraButton = (
  bokfgrupp: string | undefined,
  isNew: boolean | undefined,
  queriesLoading: boolean,
  userID: string | null,
  setRemoveRowsDialog: React.Dispatch<React.SetStateAction<boolean>>,
  setOnSendCallback: React.Dispatch<
    React.SetStateAction<{ callback: () => void }>
  >,
  utbetalningsKategori: TEDEKBokforderU_UTBETALNINGSKATEGORIEnum | null,
  type: TEDEKBokforderTYPEnum,
  setRemoveDialogWarningText: React.Dispatch<React.SetStateAction<string>>
): IEditMenuAction => {
  const bokforderWrapperContext = useContext(BokforderWrapperContext);
  const franvaroContext = useContext(FranvaroContext);

  const setAdditionalAttestDialogOpen =
    bokforderWrapperContext.setAdditionalAttestDialogOpen;
  return useMemo(
    () => ({
      id: 'sendAndAttestButton',
      isDisabled: params => {
        return getBfoValidationErrors(params) || queriesLoading;
      },
      isHidden: isSendAndAttestButtonHidden(userID ?? ''),
      label: 'Skicka vidare/Attestera',
      shortLabel: 'Skicka/Attestera',
      icon: 'check_circle_outline',
      hideIconIfNotFloating: true,
      onClick: params => {
        const rows:
          | BokforderType__Associations_Rader[]
          | UtanordningType__Associations_Rader[] =
          params.businessDataFormContext.formState.currentValues?._Associations
            ?.Rader ?? [];

        const attestRows: BokforderType__Associations_Rader[] =
          rows?.filter(
            (row: any) => row?._Associations?.Attestant?.id === userID
          ) ?? [];

        const isAttestant = attestRows.length > 0;

        const haveAttesterat = attestRows.every(
          row => row.ATTESTERAD_DATUM != null
        );

        const currentAttestanterWithCurrentUserAsReserv: IFranvaroUserOption[] =
          getAttestanterWithCurrentUserAsReserv(
            params.businessDataFormContext?.formState.currentValues
              ?._Associations?.Rader ?? [],
            franvaroContext.franvaroReservForData,
            params.businessDataFormContext?.data?.TYP ?? type
          );

        const notAttesteradeAttestanter =
          currentAttestanterWithCurrentUserAsReserv.filter(
            user => !user.Attesterat
          );
        if (isNew) {
          const newValues = getValuesForNewBokforder(
            TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST,
            bokfgrupp,
            userID,
            utbetalningsKategori,
            true,
            type
          );

          let updateValues = getRowsDeleteObjects(
            params.businessDataFormContext.formState.currentValues,
            true,
            true
          );

          updateValues = updateValues.concat(newValues);

          const unfullfilledRowsToRemove = getRowsDeleteObjects(
            params.businessDataFormContext.formState.currentValues,
            true,
            false
          );

          if (unfullfilledRowsToRemove.length > 0) {
            setRemoveRowsDialog(true);
            setRemoveDialogWarningText('Vill du attestera bokföringsordern?');
            setOnSendCallback({
              callback: () => {
                if (
                  (notAttesteradeAttestanter.length > 0 &&
                    isAttestant &&
                    !haveAttesterat) ||
                  notAttesteradeAttestanter.length > 1
                ) {
                  params.businessDataFormContext.update(updateValues);
                  setAdditionalAttestDialogOpen(
                    AdditionalAttestDialogOpenEnum.ATTESTERA
                  );
                } else {
                  updateValues = updateValues.concat(
                    getCertifiedRowsUpdateValues(
                      params.businessDataFormContext.formState.currentValues
                        ._Associations.Rader,
                      userID
                    )
                  );
                  saveRows(params, updateValues);
                }
              },
            });
          } else {
            if (
              (notAttesteradeAttestanter.length > 0 &&
                isAttestant &&
                !haveAttesterat) ||
              notAttesteradeAttestanter.length > 1
            ) {
              params.businessDataFormContext.update(updateValues);
              setAdditionalAttestDialogOpen(
                AdditionalAttestDialogOpenEnum.ATTESTERA
              );
            } else {
              const rows =
                params.businessDataFormContext.formState.currentValues
                  ._Associations.Rader;
              const today = new Date().toLocaleDateString('sv-SE');
              if (rows != null) {
                updateValues = updateValues.concat(
                  certifyOrRetakeRows(rows, userID ?? '', today)
                );
              }

              const mergedValues = mergeBusinessDataMutations(
                params.businessDataFormContext.formState.values ?? {},
                getFormUpdateValue(
                  updateValues,
                  params.businessDataFormContext.data ?? {}
                )
              );

              params.businessDataFormContext.saveForm(
                mergedValues,
                () => {
                  params.selectDetail?.(null, true);
                },
                undefined,
                { suppressAddedIdSelection: true }
              );
            }
          }
        } else {
          const date = new Date().toLocaleDateString('sv-SE');
          let values: IValues = {
            STATUS: TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST,
            STATUS_DATUM: date,
          };

          if (
            params.businessDataFormContext?.data?.BESTALLD_DATUM == undefined
          ) {
            values = {
              ...values,
              BESTALLD_DATUM: date,
            };
          }

          const newValues: FormUpdateAssociatonType[] = [
            {
              operation: 'OwnFields',
              values: values,
            },
          ];

          let updateValues = getRowsDeleteObjects(
            params.businessDataFormContext.formState.currentValues,
            true,
            true
          );

          const unfullfilledRowsToRemove = getRowsDeleteObjects(
            params.businessDataFormContext.formState.currentValues,
            true,
            false
          );

          updateValues.push(newValues);
          if (unfullfilledRowsToRemove.length > 0) {
            setRemoveRowsDialog(true);
            setRemoveDialogWarningText('Vill du attestera bokföringsordern?');
            setOnSendCallback({
              callback: () => {
                if (
                  (notAttesteradeAttestanter.length > 0 &&
                    isAttestant &&
                    !haveAttesterat) ||
                  notAttesteradeAttestanter.length > 1
                ) {
                  params.businessDataFormContext.update(updateValues);
                  setAdditionalAttestDialogOpen(
                    AdditionalAttestDialogOpenEnum.ATTESTERA
                  );
                } else {
                  updateValues = updateValues.concat(
                    getCertifiedRowsUpdateValues(
                      params.businessDataFormContext.formState.currentValues
                        ._Associations.Rader,
                      userID
                    )
                  );

                  saveRows(params, updateValues);
                }
              },
            });
          } else {
            if (
              (notAttesteradeAttestanter.length > 0 &&
                isAttestant &&
                !haveAttesterat) ||
              notAttesteradeAttestanter.length > 1
            ) {
              params.businessDataFormContext.update(updateValues);
              setAdditionalAttestDialogOpen(
                AdditionalAttestDialogOpenEnum.ATTESTERA
              );
            } else {
              const rows =
                params.businessDataFormContext.formState.currentValues
                  ._Associations.Rader;
              const today = new Date().toLocaleDateString('sv-SE');
              if (rows != null) {
                updateValues = updateValues.concat(
                  certifyOrRetakeRows(rows, userID ?? '', today)
                );
              }

              const mergedValues = mergeBusinessDataMutations(
                params.businessDataFormContext.formState.values ?? {},
                getFormUpdateValue(
                  updateValues,
                  params.businessDataFormContext.data ?? {}
                )
              );

              params.businessDataFormContext.saveForm(
                mergedValues,
                () => {
                  params.selectDetail?.(null, true);
                },
                undefined,
                { suppressAddedIdSelection: true }
              );
            }
          }
        }
      },
    }),
    [
      bokfgrupp,
      franvaroContext.franvaroReservForData,
      isNew,
      queriesLoading,
      setAdditionalAttestDialogOpen,
      setOnSendCallback,
      setRemoveDialogWarningText,
      setRemoveRowsDialog,
      type,
      userID,
      utbetalningsKategori,
    ]
  );
};
